import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Sun, Moon, Phone } from "lucide-react";
import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import { useTheme } from "../../context/ThemeContext";
import useApiData from "../../hooks/useApiData";

const navLinks = [
  { name: "Home", path: "/", sectionId: "home" },
  {
    name: "Transformations",
    path: "/transformations",
    sectionId: "transformations",
  },
  { name: "Testimonials", path: "/testimonials", sectionId: "testimonials" },
  { name: "Programs", path: "/pricing" },
  { name: "Nutrition", path: "/nutrition" },
  { name: "About", path: "/about" },
  { name: "Blog", path: "/blog" },
  { name: "FAQ", path: "/faq", sectionId: "faq" },
  { name: "Contact", path: "/contact", sectionId: "contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => setMobileOpen(false), [location]);

  const { data } = useApiData("/contact-info", { contact: {}, social: {} });
  const contact = data?.contact || {};

  const isHome = location.pathname === "/";

  const handleNavClick = (e, link) => {
    if (!link.sectionId) return;

    if (isHome) {
      const target = document.getElementById(link.sectionId);
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: "smooth", block: "start" });
        setMobileOpen(false);
        return;
      }
      e.preventDefault();
      navigate(link.path);
      setMobileOpen(false);
      return;
    }

    setMobileOpen(false);
  };

  const getLinkTarget = (link) => {
    if (!link.sectionId) return link.path;
    return isHome ? "/" : `/#${link.sectionId}`;
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? "glass py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0">
            <img
              src="/images/dvx-logo.png"
              alt="DVX Logo"
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={getLinkTarget(link)}
                onClick={(e) => handleNavClick(e, link)}
                className={`text-sm font-medium uppercase tracking-wider transition-colors ${
                  location.pathname === link.path
                    ? "text-[var(--dvx-primary)]"
                    : "text-[var(--dvx-text-muted)] hover:text-[var(--dvx-text)]"
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Desktop Right */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className={`flex items-center justify-center h-10 w-10 shrink-0
                hover:bg-transparent active:bg-transparent focus:bg-transparent
                data-[state=open]:bg-transparent
                ${
                  theme === "dark"
                    ? "hover:text-white"
                    : "hover:text-[var(--dvx-text)]"
                }
              `}
            >
              {theme === "dark" ? <Sun size={20} /> : <Moon size={20} />}
            </Button>

            {/* CTA */}
            <Link to="/contact">
              <Button className="px-6 py-3 bg-gradient-to-r from-[var(--dvx-primary)] to-[var(--dvx-primary-dark)] text-white">
                Join DVX
              </Button>
            </Link>

            {/* Call */}
            {contact?.support_phone && (
              <a href={`tel:${contact.support_phone}`}>
                <Button className="flex items-center justify-center h-10 w-10 shrink-0 text-white bg-gradient-to-r from-[var(--dvx-primary)] to-[var(--dvx-primary-dark)]">
                  <Phone size={18} />
                </Button>
              </a>
            )}
          </div>

          {/* Mobile */}
          <div className="lg:hidden flex items-center gap-2">
            {/* Theme */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className={`flex items-center justify-center h-11 w-11 shrink-0
                hover:bg-transparent active:bg-transparent focus:bg-transparent
                data-[state=open]:bg-transparent
                ${theme === "dark" ? "hover:text-white" : "hover:text-[var(--dvx-text)]"}
              `}
            >
              {theme === "dark" ? <Sun size={20} /> : <Moon size={20} />}
            </Button>

            {/* Hamburger */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center justify-center h-11 w-11 shrink-0 overflow-visible"
                >
                  {mobileOpen ? <X size={22} /> : <Menu size={22} />}
                </Button>
              </SheetTrigger>

              <SheetContent
                side="right"
                className="w-full sm:w-80 bg-[var(--dvx-surface)] border-[var(--dvx-border)]"
              >
                <div className="flex flex-col gap-6 pt-10">
                  <Link to="/" className="text-3xl font-bold">
                    DVX
                  </Link>

                  <div className="flex flex-col gap-4">
                    {navLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={getLinkTarget(link)}
                        onClick={(e) => handleNavClick(e, link)}
                        className={`py-2 border-b ${
                          location.pathname === link.path
                            ? "text-[var(--dvx-primary)] border-[var(--dvx-primary)]"
                            : "text-[var(--dvx-text-muted)] hover:text-[var(--dvx-text)]"
                        }`}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>

                  <Link to="/contact">
                    <Button className="w-full py-4 bg-gradient-to-r from-[var(--dvx-primary)] to-[var(--dvx-primary-dark)] text-white">
                      Join DVX
                    </Button>
                  </Link>

                  {contact?.support_phone && (
                    <a href={`tel:${contact.support_phone}`}>
                      <Button className="w-full py-4 bg-gradient-to-r from-[var(--dvx-primary)] to-[var(--dvx-primary-dark)] text-white">
                        Call Now
                      </Button>
                    </a>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
}
